import React, { useEffect, useMemo, useRef, useState } from "react";
import { FlatList, StyleSheet, View } from "react-native";
import {
  ActivityIndicator,
  Button,
  Dialog,
  List,
  Portal,
  Text,
} from "react-native-paper";
import { useTranslation } from "react-i18next";
import { OutlinedInput } from "./fields/OutlinedInput";
import { getDocuments } from "../services/firestoreServices";
import { COMPANIES_COLLECTION } from "../constants/collections";
import type { Company } from "../redux/slices/authSlice";

type CompanyOption = Pick<Company, "id" | "name">;

interface CompanyPickerDialogProps {
  visible: boolean;
  selectedCompany: CompanyOption | null;
  onDismiss: () => void;
  onSelect: (company: CompanyOption) => void;
}

export default function CompanyPickerDialog({
  visible,
  selectedCompany,
  onDismiss,
  onSelect,
}: CompanyPickerDialogProps) {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const isSelectingRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      isSelectingRef.current = false;
      return;
    }

    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const data = (await getDocuments(
          COMPANIES_COLLECTION,
        )) as CompanyOption[];
        const mapped = data
          .map((item) => ({
            id: String(item.id),
            name: String(item.name),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(mapped);
      } catch (error) {
        console.error("[COMPANY PICKER], failed to load companies", error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    setSearchQuery("");
    void fetchCompanies();
  }, [visible]);

  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;

    const query = searchQuery.toLowerCase();
    return companies.filter((item) => item.name.toLowerCase().includes(query));
  }, [companies, searchQuery]);

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
        <Dialog.Title>{t("Select company")}</Dialog.Title>
        <Dialog.Content>
          <OutlinedInput
            label="Search"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />

          <View style={styles.listContainer}>
            {loading ? (
              <ActivityIndicator style={styles.loader} />
            ) : (
              <FlatList
                data={filteredCompanies}
                keyExtractor={(item) => item.id}
                ListEmptyComponent={() => (
                  <Text style={styles.emptyText}>{t("Nothing found")}</Text>
                )}
                renderItem={({ item }) => (
                  <List.Item
                    title={item.name}
                    onPress={() => {
                      if (isSelectingRef.current) return;
                      isSelectingRef.current = true;
                      onSelect(item);
                      onDismiss();
                    }}
                    right={() =>
                      selectedCompany?.id === item.id ? (
                        <List.Icon icon="check" />
                      ) : null
                    }
                  />
                )}
              />
            )}
          </View>
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss}>{t("Cancel")}</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
}

const styles = StyleSheet.create({
  dialog: {
    maxHeight: "80%",
  },
  listContainer: {
    marginTop: 12,
    minHeight: 220,
    maxHeight: 360,
  },
  loader: {
    marginTop: 16,
  },
  emptyText: {
    textAlign: "center",
    marginTop: 16,
  },
});
